import { useId } from 'react'
import { Field, Form } from 'react-final-form'
import { z } from 'zod'
import { ArrowCta } from '@mc/certificates-design-system'
import {
  IconCheckDefault,
  InputField,
  Modal,
  ModalClose,
  ModalContent,
} from '@mc/design-system'
import { createValidator } from '@mc/form-utils'
import { useTranslation } from '@mc/i18n'
import { useFireEvent } from 'pages/certificates/tracking'
import { emailSchema, withoutFieldErrors } from 'pages/certificates/validations'
import { submitNotifyRequest } from '../submitNotifyRequest'
import { useRememberedSubmission } from '../useRememberedSubmission'

type FormValues = {
  notifyEmail: string
}

// Not `email`: the syllabus form on the same page already owns the `email` id
const validate = createValidator(z.object({ notifyEmail: emailSchema }))

type CohortNotifyModalProps = {
  slug: string
  show: boolean
  onClose: () => void
}

export const CohortNotifyModal = ({
  slug,
  show,
  onClose,
}: CohortNotifyModalProps) => {
  const { t } = useTranslation('@mc/certificates')
  const headingId = useId()
  const fireEvent = useFireEvent()
  const { status, headingRef, formRef, submitEmail, showForm } =
    useRememberedSubmission({
      submission: 'notify',
      slug,
      emailFieldName: 'notifyEmail',
      request: submitNotifyRequest,
      failureMessage: 'Cohort notify request failed',
      active: show,
    })

  const handleSubmit = ({ notifyEmail: email }: FormValues) =>
    submitEmail(email, () =>
      fireEvent('certificateEmailCaptured', {
        action: t('marketingPages.cohorts.notify.cta'),
        location: 'coming soon',
        email,
        campaign: slug,
      }),
    )

  const isThankYou =
    status.kind === 'success' || status.kind === 'alreadyRequested'

  return (
    <Modal show={show} onClose={onClose}>
      <div className='certificates-theme'>
        <div className='mc-theme-light'>
          <ModalContent
            aria-labelledby={headingId}
            className='mx-auto rounded-lg bg-c-base-100 px-6 py-7 text-center text-c-text-dark lg:w-1/2 lg:px-7'
          >
            <ModalClose />
            <div className='flex flex-col items-center gap-5'>
              {isThankYou && (
                <span className='flex size-7 items-center justify-center rounded-full bg-c-brand-highlight'>
                  <IconCheckDefault size='sm' />
                </span>
              )}
              <h2
                id={headingId}
                ref={headingRef}
                tabIndex={-1}
                className='c-heading-2 c-text-ivar-display-condensed m-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
              >
                {isThankYou
                  ? t('marketingPages.cohorts.notify.successTitle')
                  : t('marketingPages.cohorts.notify.title')}
              </h2>
              <p className='c-body-large m-0 text-balance'>
                {isThankYou
                  ? t('marketingPages.cohorts.notify.successBody')
                  : t('marketingPages.cohorts.notify.body')}
              </p>
              {status.kind === 'alreadyRequested' && (
                <button
                  type='button'
                  className='c-body-bold cursor-pointer border-0 bg-transparent p-0 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
                  onClick={showForm}
                >
                  {t('marketingPages.cohorts.notify.useDifferentEmail')}
                </button>
              )}
            </div>

            {!isThankYou && (
              <Form<FormValues>
                validate={validate}
                onSubmit={handleSubmit}
                render={({
                  handleSubmit: submit,
                  hasValidationErrors,
                  submitting,
                }) => (
                  <form
                    ref={formRef}
                    noValidate
                    className='mt-5 flex flex-col gap-3 text-left'
                    onSubmit={submit}
                  >
                    <Field
                      name='notifyEmail'
                      type='email'
                      placeholder={t(
                        'marketingPages.cohorts.notify.emailPlaceholder',
                      )}
                      autoComplete='email'
                      required
                      render={({ input, meta, ...rest }) => (
                        <InputField
                          {...rest}
                          input={input}
                          meta={withoutFieldErrors(meta)}
                          label={t('downloadSyllabus.emailAddress')}
                        />
                      )}
                    />
                    <ArrowCta
                      type='submit'
                      kind='primary'
                      disabled={hasValidationErrors}
                      loading={submitting}
                    >
                      {t('marketingPages.cohorts.notify.cta')}
                    </ArrowCta>
                    {status.kind === 'error' && (
                      <p role='alert' className='c-body-small m-0'>
                        {t('marketingPages.cohorts.notify.error')}
                      </p>
                    )}
                  </form>
                )}
              />
            )}
          </ModalContent>
        </div>
      </div>
    </Modal>
  )
}
